import { useEffect } from "react";
import { useQuery } from "react-query";
import { usePunditUserContext } from "@circle-react/contexts";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useTipTapEditorContext } from "../TipTapEditorContext";

interface LinkPreview {
  description: string;
  title: string;
  url: string;
}

export const useUnfurlLink = ({
  linkHrefs,
  setHasLinks,
}: {
  linkHrefs: string[];
  setHasLinks: (hasLinks: boolean) => void;
}) => {
  const { linkPreviews, setLinkPreviews, editor } = useTipTapEditorContext();
  const { currentCommunitySettings } = usePunditUserContext();
  const { remove_link_previews_enabled: isRemoveLinkPreviewsEnabled } =
    currentCommunitySettings || {};

  const hasAttributesForPreview = (linkPreview: LinkPreview) =>
    linkPreview.title || linkPreview.description;

  const setPreviewLinks = (urls: any[]) => {
    // Create new linkPreviews object with only URLs that are in linkHrefs
    const updatedLinkPreviews = Object.keys(linkPreviews)
      .filter(url => linkHrefs.includes(url))
      .reduce(
        (acc, url) => {
          acc[url] = linkPreviews[url];
          return acc;
        },
        {} as Record<string, boolean>,
      );

    const newLinks = urls.filter(
      item => !Object.keys(linkPreviews).includes(item.url),
    );

    // Add new links
    const finalLinkPreviews = {
      ...updatedLinkPreviews,
      ...newLinks.reduce(
        (acc, item) => {
          acc[item.url] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    };

    setLinkPreviews(finalLinkPreviews);
  };

  const { data: unfurledLinks = [], isLoading } = useQuery(
    ["unfurlURLs", linkHrefs],
    () =>
      reactQueryGet(
        internalApi.richTextFields.unfurlURLs({ params: { urls: linkHrefs } }),
      ),
    {
      enabled: linkHrefs.length > 0,
      // This is to prevent empty screen when new items are fetched
      keepPreviousData: true,
      initialData: [],
      onSuccess: data => {
        const urls = data.filter(
          (unfurledLink: any) =>
            linkHrefs.includes(unfurledLink.url) &&
            hasAttributesForPreview(unfurledLink),
        );
        const hasLinks = urls.length > 0;
        setHasLinks(hasLinks);
      },
    },
  );

  useEffect(() => {
    if (isRemoveLinkPreviewsEnabled && editor.isEditable) {
      const urls = unfurledLinks.filter(
        (unfurledLink: any) =>
          linkHrefs.includes(unfurledLink.url) &&
          hasAttributesForPreview(unfurledLink),
      );
      setPreviewLinks(urls);
    }
  }, [
    unfurledLinks,
    editor.isEditable,
    isRemoveLinkPreviewsEnabled,
    linkHrefs.length,
  ]);

  const filteredUnfurledLinks = isRemoveLinkPreviewsEnabled
    ? unfurledLinks.filter(
        (unfurledLink: any) =>
          !Object.keys(linkPreviews).includes(unfurledLink.url) ||
          Object.keys(linkPreviews).some(
            (url: any) =>
              url === unfurledLink.url && linkPreviews[url] === true,
          ),
      )
    : unfurledLinks;

  return {
    unfurledLinks,
    isLoading,
    filteredUnfurledLinks,
    isRemoveLinkPreviewsEnabled,
  };
};
